import { cn, humanizeDate } from '@/utils'
import { ticketSnippet } from '../messagePreviews'
import type { SupportTicket } from '../types'
import { SupportPill } from './SupportPill'

interface TicketListRowProps {
  ticket: SupportTicket
  selected: boolean
  unread: boolean
  onSelect: (publicId: string) => void
}

export const TicketListRow = ({
  ticket,
  selected,
  unread,
  onSelect,
}: TicketListRowProps) => {
  const snippet = ticketSnippet(ticket)

  return (
    <button
      type="button"
      onClick={() => onSelect(ticket.publicId)}
      className={cn(
        'w-full cursor-pointer rounded-lg border-l-[3px] px-3 py-2.5 text-left transition-colors',
        ticket.status === 'awaiting_user'
          ? 'border-l-amber-400'
          : 'border-l-transparent',
        // Selection is structure, not status, so amber stays the only status
        // colour in the list: the selected row lifts to a white card on the
        // tinted panel. A ring rather than an outline, or the selected row
        // would lose its keyboard focus ring.
        selected
          ? 'bg-white shadow-sm ring-1 ring-inset ring-blue-3'
          : 'hover:bg-white/60',
      )}
    >
      <div className="flex items-baseline gap-2">
        <span
          className={cn(
            'flex-1 truncate text-sm text-blue-7',
            unread ? 'font-semibold' : 'font-medium',
          )}
        >
          {ticket.subject}
        </span>
        {unread && (
          <>
            <span className="h-2 w-2 shrink-0 self-center rounded-full bg-brand-deep" />
            <span className="sr-only">Unread activity</span>
          </>
        )}
        <span className="shrink-0 text-xs text-ink-subtle">
          {humanizeDate(ticket.lastMessageAt)}
        </span>
      </div>

      {snippet && (
        <p className="mt-1 truncate text-xs text-ink-subtle">{snippet}</p>
      )}

      {ticket.projectName && (
        <p className="mt-1.5 overflow-hidden">
          <SupportPill tone="neutral">{ticket.projectName}</SupportPill>
        </p>
      )}
    </button>
  )
}
